import { Component, h, createRef, RefObject } from 'preact';
import cx from 'classnames';

import { HopplerModalizer } from '../../common/hoppler-modalizer.component';

import { NATIONALITIES } from '../../../../../constants/nationalities';
import { LEAD_SOURCES } from '../../../../../constants/inquiries';

import { ReqState } from '../../../../../types';
import { Form } from '../../../../utils/forms/form';
import { cxValidateField } from '../../../../utils/forms/validators';

import { Inquiry } from '../../../../../types/inquiries';
import { LabeledChoiceButtonGroup } from '../../common/labeled-choice-button-group.component';
import { AddClientFastTrackForm } from './add-client-fast-track-form.component';
import { AddClientPropertySelect } from './add-client-property-select.component';
import { RadioGroup } from '../../common/radio-group.component';
import { InqStageTransitionContent } from '../documents/inq-stage-transition-content.component';
import { FastTrackModalMode } from './mode';

interface AddClientModalProps {
  form: Form;
  scrollerRef?: RefObject<HTMLDivElement>;
  inputRefs: { [key: string]: RefObject<HTMLInputElement> };

  /**
   * The inquiry associated with the FTF being edited, if in FTF edit mode.
   * If in Add Client mode, set this to null or undefined.
   */
  inquiry?: Inquiry;

  submitState: ReqState;
  mode: FastTrackModalMode;

  onFieldChange(fieldName: string, value: any);
  onSubmit: () => void;
  onProceed: () => void;
  onClose: () => void;
}

export class AddClientModal extends Component<AddClientModalProps> {
  formScrollerRef = createRef<HTMLDivElement>();

  createChangeHandler(fieldName: string) {
    return (event: Event) => {
      const element = event.target as HTMLInputElement;
      this.props.onFieldChange(fieldName, element.value);
    }
  }

  getPropertyInfoInputs(props: AddClientModalProps) {
    const form = props.form;
    const hasSpecificProperties = form.fields.hasSpecificProperties.value;

    if (hasSpecificProperties) {
      return (
        <div className="hi-form-take-row">
          <AddClientPropertySelect
            propertyKeys={ form.fields.selectedProperties.value }
            ref={ props.inputRefs.selectedProperties }
            showInvalidState={ cxValidateField(form.fields.selectedProperties) }
            onChange={ keys => props.onFieldChange('selectedProperties', keys) } />
        </div>
      );
    } else {
      return (
        <div className="hi-form-take-row">
          <AddClientFastTrackForm
            form={ props.form }
            inputRefs={ props.inputRefs }
            onFieldChange={ props.onFieldChange } />
        </div>
      );
    }
  }


  getSaveButton(props: AddClientModalProps) {
    const buttonText = props.mode === FastTrackModalMode.ADD_CLIENT ? 'Add Client' : 'Save Changes';

    return (
      <button className="hi-form-button btn btn-secondary"
        onClick={ props.onSubmit }
        disabled={ props.submitState === ReqState.IN_PROGRESS }>
        { props.submitState === ReqState.IN_PROGRESS ? 
          <i className="fas fa-cog fa-spin" /> :
          buttonText
        }
      </button>
    )
  }

  getFormArea(props: AddClientModalProps) {
    const form = props.form;

    if (props.submitState !== ReqState.SUCCESS) {
      return (
        <div className="hi-modal__body overflow-hidden">
          <div className="hi-modal__scroll-area flex-fill pb-0" ref={ props.scrollerRef }>
            <div className="hi-form-grid">
              <div className="hi-input-group">
                <label className="hi-input-group__label">Client's Full Name</label>
                <div className="d-flex">
                  <input type="text" placeholder="First name"
                    className={ cx('form-control w-50 hi-border-radius-right-0', {
                      'is-invalid': cxValidateField(form.fields.clientFirstName)
                    }) }
                    value={ form.fields.clientFirstName.value }
                    ref={ props.inputRefs.clientFirstName }
                    onChange={ this.createChangeHandler('clientFirstName') } />
                  <input type="text" placeholder="Last name"
                    className={ cx('form-control w-50 hi-border-radius-left-0', {
                      'is-invalid': cxValidateField(form.fields.clientLastName)
                    }) }
                    value={ form.fields.clientLastName.value }
                    ref={ props.inputRefs.clientLastName }
                    onChange={ this.createChangeHandler('clientLastName') } />
                </div>
              </div>
  
              <div className="hi-input-group">
                <label className="hi-input-group__label">Mobile number</label>
                <input type="tel"
                  className={ cx('form-control', {
                    'is-invalid': cxValidateField(form.fields.mobile)
                  }) }
                  placeholder="00000000000"
                  value={ form.fields.mobile.value }
                  ref={ props.inputRefs.mobile }
                  onChange={ this.createChangeHandler('mobile') } />
              </div>
  
              <div className="hi-input-group">
                <label className="hi-input-group__label">Email Address</label>
                <input type="email"
                  className={ cx('form-control', {
                    'is-invalid': cxValidateField(form.fields.email)
                  }) }
                  placeholder="Email address"
                  value={ form.fields.email.value }
                  ref={ props.inputRefs.email }
                  onChange={ this.createChangeHandler('email') } />
              </div>
  
              <div className="hi-input-group">
                <label className="hi-input-group__label">Nationality</label>
                <select 
                  value={ form.fields.nationality.value }
                  ref={ props.inputRefs.nationality }
                  className={ cx('form-control', {
                    'is-invalid': cxValidateField(form.fields.nationality)
                  }) }
                  onChange={ this.createChangeHandler('nationality') }>
                  <option value={ null } disabled>Select</option>
                  { NATIONALITIES.map(
                    nationality => <option value={ nationality }>{ nationality }</option>
                  ) }
                </select>
              </div>
  
              {/* span-2 Client is a ... */}
              <div className="hi-input-group hi-form-take-row">
                <label className="hi-input-group__label">Client is a</label>
                <RadioGroup value={ form.fields.designation.value }
                  choices={[
                    { label: 'Buyer/Renter', value: 1 },
                    { label: 'Broker/Agent', value: 2 },
                    { label: 'Other', value: 10 }
                  ]}
                  showInvalidState={ cxValidateField(form.fields.designation) }
                  onSelect={ choice => props.onFieldChange('designation', choice) }
              
                  layout={{ columns: 2 }}
                  layoutLg={{ flex: true }} />
              </div>

              { form.fields.designation.value === 10 && (
                <div className="hi-input-group hi-form-take-row">
                  <label className="hi-input-group__label">
                    Other Designation
                  </label>
                  <input type="text"
                    placeholder="Other designation"
                    className={ cx('form-control', {
                      'is-invalid': cxValidateField(form.fields.designationOther)
                    }) }
                    value={ form.fields.designationOther.value }
                    onChange={ this.createChangeHandler('designationOther') }
                    ref={ props.inputRefs.designationOther } />
                </div>
              )}
  
              {/* Additional required field not in mockup: Receipts */}
              <div className="hi-input-group hi-form-take-row">
                <label className="hi-input-group__label">Does the client need official receipts?</label>
                <RadioGroup value={ form.fields.receipts.value }
                  choices={[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                  ]}
                  showInvalidState={ cxValidateField(form.fields.receipts) }
                  onSelect={ choice => props.onFieldChange('receipts', choice) }
              
                  layout={{ flex: true }}
                  layoutLg={{ flex: true }} />
              </div>
  
              {/*
                Additional required field not in mockup: Payment Term
                (Bank Financing / Cash or Post-Dated Checks)
              */}
              <div className="hi-input-group hi-form-take-row">
                <label className="hi-input-group__label">Payment term</label>
                <RadioGroup value={ form.fields.paymentTerm.value }
                  choices={[
                    { label: 'Bank Financing', value: 1 },
                    { label: 'Cash or Post-dated checks', value: 2 },
                  ]}
                  showInvalidState={ cxValidateField(form.fields.paymentTerm) }
                  onSelect={ choice => props.onFieldChange('paymentTerm', choice) }
                  ref={ props.inputRefs.paymentTerm }
              
                  layout={{ columns: 1 }}
                  layoutLg={{ flex: true }} />
              </div>
  
              { props.mode === FastTrackModalMode.ADD_CLIENT && (
                <div className="hi-input-group">
                  <label className="hi-input-group__label">Source</label>
                  <select
                    className={ cx('form-control', {
                      'is-invalid': cxValidateField(form.fields.source)
                    }) }
                    value={ form.fields.source.value }
                    ref={ props.inputRefs.source }
                    onChange={ this.createChangeHandler('source') }>
                    <option value={ null } disabled>Select</option>
                    { LEAD_SOURCES.map(source => (
                      <option value={ source.value }>
                        { source.name }
                      </option>
                    )) }
                  </select>
                </div>
              ) }

              { props.mode === FastTrackModalMode.ADD_CLIENT && ( 
                <div className="hi-input-group">
                  <label className="hi-input-group__label" style="margin-right: -20px;">
                    Does the client want specific properties?
                  </label>
                  <LabeledChoiceButtonGroup
                    value={ form.fields.hasSpecificProperties.value } 
                    choices={[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false }
                    ]}
                    onSelect={ choice => props.onFieldChange('hasSpecificProperties', choice) } />
                </div>
              ) }
  
              { this.getPropertyInfoInputs(props) }
            </div>
          </div>

          <div className="d-flex justify-content-center bg-white hi-py-20 position-sticky hi-bottom-0">
            { this.getSaveButton(props) }
          </div>
        </div>
      ); 
    }
  }

  getSuccessPanel(props: AddClientModalProps) {
    const inquiry = props.inquiry;
    const ftfMode = props.mode === FastTrackModalMode.FTF;
    const headingSnippet = ftfMode ? 'Saved FTF Changes' : 'Added a Client';
    const bodyText = ftfMode ?
      `You will find the updated inquiry in ${inquiry.stage}.` :
      'You will find the newly created lead in your pipeline';
    const targetName = ftfMode ? inquiry.stage : 'Pipeline';

    if (props.submitState === ReqState.SUCCESS) {
      return (
        <div className="hi-modal__body d-flex align-items-center justify-content-center">
          <InqStageTransitionContent
            headingText={ `Successfully ${headingSnippet}` }
            customBodyText={ bodyText }
            targetName={ targetName }

            onClose={ props.onClose }
            onProceed={ props.onProceed } />
        </div>
      );
    }
  }

  render(props: AddClientModalProps) {
    return (
      <HopplerModalizer onClose={ () => null }>
        <div className="hi-modal hi-addclmod">
          <div className="hi-modal__title-bar">
            <span className="hi-modal__title-label">
              { props.mode === FastTrackModalMode.ADD_CLIENT ? 'Add Client' : 'Edit FTF' }
            </span>
            <button className="btn btn-link hi-modal__title-close" onClick={ props.onClose }>
              <i className="fas fa-times" /> Close
            </button>
          </div>
          { this.getFormArea(props) }
          { this.getSuccessPanel(props) }
        </div>
      </HopplerModalizer>
    );
  }
}
